import { Mood, SecondaryMood } from './Mood';
import {
  GradeBand,
  UrgencyState,
  UrgencyTier,
  classifyCheckin,
  countRecentHighConcernCheckins,
  tierFromUrgencyState,
} from './EmotionsClassification';

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

export interface EmotionsReportCheckin {
  feeling: Mood | SecondaryMood;
  createdAt: number;
}

export interface EmotionsReportStudent {
  userId: string;
  firstName: string;
  lastName: string;
  grade: number;
  emotionsUrgency: UrgencyState | undefined;
  checkins: EmotionsReportCheckin[];
}

export interface EmotionsReportClassroom {
  classroomId: string;
  classroomName: string;
  grade: number;
  teacherName: string;
  students: EmotionsReportStudent[];
}

export interface DateRange {
  start: number;
  end: number;
}

export interface EmotionsReportRow {
  recentFeelings: (Mood | SecondaryMood)[];
  downCount: number;
  totalCount: number;
  last30DaysLowCount: number;
  urgencyTier: UrgencyTier | null;
}

export function deriveEmotionsReportRow(
  allCheckins: EmotionsReportCheckin[],
  gradeBand: GradeBand,
  urgencyState: UrgencyState | undefined,
  selectedRange: DateRange,
  now: number = Date.now(),
): EmotionsReportRow {
  const inRange = allCheckins
    .filter(
      (checkin) =>
        checkin.createdAt >= selectedRange.start && checkin.createdAt <= selectedRange.end,
    )
    .sort((a, b) => b.createdAt - a.createdAt);

  // Down/Total is fixed to "since class was created" - unaffected by the date filter.
  const allSorted = [...allCheckins].sort((a, b) => b.createdAt - a.createdAt);
  const downCount = allSorted.filter(
    (checkin) => classifyCheckin(checkin.feeling, gradeBand) !== 'not-concerning',
  ).length;

  const thirtyDaysAgo = now - THIRTY_DAYS_MS;
  const last30DaysLowCount = allCheckins.filter(
    (checkin) =>
      checkin.createdAt >= thirtyDaysAgo &&
      checkin.createdAt <= now &&
      classifyCheckin(checkin.feeling, gradeBand) !== 'not-concerning',
  ).length;

  const recentDownCheckins = inRange.filter(
    (checkin) => classifyCheckin(checkin.feeling, gradeBand) !== 'not-concerning',
  );

  const recentHighConcernCount = countRecentHighConcernCheckins(allCheckins, gradeBand, now);

  return {
    recentFeelings: recentDownCheckins.slice(0, 3).map((checkin) => checkin.feeling),
    downCount,
    totalCount: allSorted.length,
    last30DaysLowCount,
    urgencyTier: tierFromUrgencyState({
      consecutiveDownStreak: urgencyState?.consecutiveDownStreak ?? 0,
      highConcernCount: recentHighConcernCount,
    }),
  };
}
